import axios from 'axios';
import { ClientError } from './errors.js';
/**
 * Owner Olympus client
 */
class ClientInstance {
    instance;
    constructor(baseURL, getRequestSettings, { defaultHeaders, onRequestError, onResponseError, formatRequest, formatResponse, timeout, } = {}) {
        const headers = {
            Accept: 'application/json',
            ...defaultHeaders,
        };
        this.instance = axios.create({
            baseURL,
            headers,
            withCredentials: true,
        });
        if (timeout) {
            this.instance.defaults.timeout = timeout;
        }
        this.instance.interceptors.request.use(async (req) => {
            const { audience, audienceVersion, client, csrfToken, activeBrandId, bearerToken, activeLocationId, } = await getRequestSettings();
            if (audience) {
                req.headers['X-PB-Audience'] =
                    req.headers?.['X-PB-Audience'] ?? audience;
            }
            if (audienceVersion) {
                req.headers['X-PB-Audience-Version'] =
                    req.headers?.['X-PB-Audience-Version'] ?? audienceVersion;
            }
            if (client) {
                req.headers['X-PB-Client'] = req.headers?.['X-PB-Client'] ?? client;
            }
            if (csrfToken) {
                req.headers['X-CSRFToken'] = req.headers?.['X-CSRFToken'] ?? csrfToken;
            }
            if (bearerToken) {
                req.headers.authorization = req.headers?.Authorization
                    ? [`Bearer ${bearerToken}`, req.headers?.Authorization].join(', ')
                    : `Bearer ${bearerToken}`;
            }
            if (activeBrandId) {
                req.headers['X-Active-Brand-ID'] =
                    req.headers?.['X-Active-Brand-ID'] ?? activeBrandId;
            }
            if (activeLocationId) {
                req.headers['X-Active-Location-ID'] =
                    req.headers?.['X-Active-Location-ID'] ?? activeLocationId;
            }
            if (formatRequest)
                req = await formatRequest(req);
            return req;
        }, onRequestError);
        if (formatResponse || onResponseError)
            this.instance.interceptors.response.use(formatResponse, async (error) => {
                if (!onResponseError) {
                    return await Promise.reject(error);
                }
                return await onResponseError(error, async (config) => {
                    return await this.instance.request(config);
                });
            });
    }
    async exec(method, path, req, knownErrors, responseType) {
        try {
            const { data } = await this.send(method, path, req, responseType);
            return (responseType === 'arraybuffer' ? this.toArrayBuffer(data) : data);
        }
        catch (error) {
            const errorData = axios.isAxiosError(error)
                ? responseType === 'arraybuffer'
                    ? this.parseJsonBuffer(error.response?.data)
                    : error.response?.data
                : undefined;
            if (axios.isAxiosError(error) &&
                error.response &&
                responseType === 'arraybuffer') {
                error.response.data = errorData;
            }
            if (axios.isAxiosError(error) && knownErrors.includes(errorData?.code)) {
                const { code, message, status, payload } = errorData;
                throw new ClientError(code, message, status, payload);
            }
            throw error;
        }
    }
    async send(method, basePath, req, responseType) {
        const options = {
            headers: req.headers,
            responseType,
        };
        if (req.query && Object.keys(req.query).length) {
            options.params = req.query;
        }
        if (req.signal) {
            options.signal = req.signal;
        }
        const path = this.formatPath(basePath, req);
        switch (method) {
            case 'DELETE':
                return await this.instance.delete(path, options);
            case 'GET':
                return await this.instance.get(path, options);
            case 'PATCH':
                return await this.instance.patch(path, req.body, options);
            case 'POST':
                return await this.instance.post(path, req.body, options);
            case 'PUT':
                return await this.instance.put(path, req.body, options);
        }
    }
    toArrayBuffer(data) {
        if (data instanceof ArrayBuffer)
            return data;
        if (ArrayBuffer.isView(data)) {
            return new Uint8Array(data.buffer, data.byteOffset, data.byteLength).slice().buffer;
        }
        throw new TypeError('Expected an ArrayBuffer response');
    }
    parseJsonBuffer(data) {
        try {
            return JSON.parse(new TextDecoder().decode(this.toArrayBuffer(data)));
        }
        catch {
            return data;
        }
    }
    formatPath(path, request) {
        for (const [key, value] of Object.entries(request.params ?? {})) {
            path = path.replace(`{${key}}`, value);
        }
        return path;
    }
}
export default ClientInstance;
//# sourceMappingURL=instance.js.map